import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Upload, Trash2, Edit, Download, FileSpreadsheet, Send, CheckCircle2, Receipt, Paperclip, BarChart3 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const fmtEur = (n) => `€ ${(Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';
const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const STATUS_META = {
  bozza: { label: 'Bozza', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  inviato: { label: 'Inviato', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  approvato: { label: 'Approvato', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  fatturato: { label: 'Fatturato', cls: 'bg-green-50 text-green-700 border-green-200' },
};

const emptyLine = () => ({ code: '', description: '', unit: 'nr', quantity: 0, unit_price: 0, k_line_pct: 0 });
const emptyForm = () => ({ sal_number: '', work_order: '', purchase_order: '', description: '', site_id: '', project_id: '', date: new Date().toISOString().slice(0, 10), period: '', lines: [emptyLine()], k_total_pct: 0, vat_pct: 22, vat_included: false, notes: '' });

export default function SALFiumicinoPage() {
  const { isRole } = useAuth();
  const canManage = isRole('admin', 'qhse', 'capo_cantiere');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '__all__', site_id: '__all__', work_order: '' });

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useRef(null);
  const attachRef = useRef(null);

  // Monthly totals
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthly, setMonthly] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== '__all__') params.status = filters.status;
      if (filters.site_id !== '__all__') params.site_id = filters.site_id;
      if (filters.work_order) params.work_order = filters.work_order;
      const [listRes, statsRes] = await Promise.all([
        api.get('/sal-fiumicino', { params }),
        api.get('/sal-fiumicino/stats'),
      ]);
      setItems(listRes.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadMeta = async () => {
    try {
      const [s, p] = await Promise.all([api.get('/sites'), api.get('/projects')]);
      setSites(s.data || []);
      setProjects(p.data || []);
    } catch (e) { console.error(e); }
  };

  const loadMonthly = async (y) => {
    try {
      const res = await api.get('/sal-fiumicino/monthly-totals', { params: { year: y } });
      setMonthly(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => { loadMonthly(year); }, [year]);

  // Form helpers
  const setLine = (i, key, val) => setForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [key]: val } : l) }));
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const formTotal = form.lines.reduce((s, l) => {
    const base = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
    return s + base + base * (Number(l.k_line_pct) || 0) / 100;
  }, 0);

  const openNew = () => { setEditingId(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({
      sal_number: m.sal_number || '', work_order: m.work_order || '', purchase_order: m.purchase_order || '',
      description: m.description || '', site_id: m.site_id || '', project_id: m.project_id || '',
      date: m.date || new Date().toISOString().slice(0, 10), period: m.period || '',
      lines: (m.lines && m.lines.length ? m.lines.map(l => ({ ...l })) : [emptyLine()]),
      k_total_pct: m.k_total_pct || 0, vat_pct: m.vat_pct ?? 22, vat_included: !!m.vat_included, notes: m.notes || '',
    });
    setDetail(null);
    setShowForm(true);
  };

  const save = async () => {
    const payload = { ...form, site_id: form.site_id === '__none__' ? '' : form.site_id, project_id: form.project_id === '__none__' ? '' : form.project_id };
    try {
      if (editingId) await api.put(`/sal-fiumicino/${editingId}`, payload);
      else await api.post('/sal-fiumicino', payload);
      setShowForm(false);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Errore salvataggio'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminare questo SAL?')) return;
    try { await api.delete(`/sal-fiumicino/${id}`); setDetail(null); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true); setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/sal-fiumicino/upload-excel', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(res.data);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Errore upload'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const doSubmit = async (id) => { await api.post(`/sal-fiumicino/${id}/submit`); refreshDetail(id); load(); };
  const doApprove = async (id) => {
    const name = window.prompt('Nome di chi approva il SAL:');
    if (name === null) return;
    await api.post(`/sal-fiumicino/${id}/approve`, { approver_name: name }); refreshDetail(id); load();
  };
  const doLinkInvoice = async (id) => {
    const num = window.prompt('Numero fattura:');
    if (!num) return;
    await api.post(`/sal-fiumicino/${id}/link-invoice`, { invoice_number: num }); refreshDetail(id); load();
  };
  const doRevoke = async (id, type) => {
    if (!window.confirm('Confermi la revoca?')) return;
    await api.post(`/sal-fiumicino/${id}/revoke`, { type }); refreshDetail(id); load();
  };

  const refreshDetail = async (id) => {
    try { const res = await api.get(`/sal-fiumicino/${id}`); setDetail(res.data); } catch {}
  };

  const handleAttach = async (file) => {
    if (!file || !detail) return;
    const fd = new FormData(); fd.append('file', file);
    try { await api.post(`/sal-fiumicino/${detail.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); refreshDetail(detail.id); }
    catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { if (attachRef.current) attachRef.current.value = ''; }
  };

  const exportExcel = (id) => {
    const token = localStorage.getItem('auth_token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sal-fiumicino/${id}/export-excel`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(b => { const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = `SAL_Fiumicino_${id}.xlsx`; a.click(); URL.revokeObjectURL(u); });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sal-fiumicino-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><FileSpreadsheet size={24} /> SAL Fiumicino</h1>
          <p className="text-sm text-slate-500">Stato Avanzamento Lavori cantieri in seram - Fiumicino</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-excel-btn">
              <Upload size={16} className="mr-1" /> {uploading ? 'Caricamento...' : 'Carica Excel'}
            </Button>
            <Button onClick={openNew} data-testid="new-sal-btn"><Plus size={16} className="mr-1" /> Nuovo SAL</Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="elenco">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="elenco" data-testid="tab-elenco">Elenco SAL</TabsTrigger>
          <TabsTrigger value="totali" data-testid="tab-totali" className="flex items-center gap-1"><BarChart3 size={14} /> Totali Cantieri Mensili</TabsTrigger>
        </TabsList>

        {/* ELENCO */}
        <TabsContent value="elenco" className="space-y-4">
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(STATUS_META).map(([k, meta]) => (
                <Card key={k} className="border-slate-200 shadow-sm" data-testid={`stat-${k}`}>
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500">{meta.label}</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.counts?.[k] || 0}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {stats && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Pipeline Fatturazione</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div><p className="text-xs text-slate-500">In compilazione</p><p className="font-bold text-slate-700">{fmtEur(stats.pipeline?.in_compilazione)}</p></div>
                <div><p className="text-xs text-slate-500">Inviati</p><p className="font-bold text-blue-700">{fmtEur(stats.pipeline?.inviati)}</p></div>
                <div><p className="text-xs text-slate-500">Approvati</p><p className="font-bold text-amber-700">{fmtEur(stats.pipeline?.approvati)}</p></div>
                <div><p className="text-xs text-slate-500">Fatturati</p><p className="font-bold text-green-700">{fmtEur(stats.pipeline?.fatturati)}</p></div>
              </CardContent>
            </Card>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-end">
            <div className="space-y-1"><Label className="text-xs">Stato</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
                <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti gli stati</SelectItem>
                  {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Cantiere</Label>
              <Select value={filters.site_id} onValueChange={(v) => setFilters(f => ({ ...f, site_id: v }))}>
                <SelectTrigger className="w-48" data-testid="filter-site"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti i cantieri</SelectItem>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Work Order</Label>
              <Input className="w-40" value={filters.work_order} onChange={(e) => setFilters(f => ({ ...f, work_order: e.target.value }))} placeholder="Cerca WO" data-testid="filter-wo" />
            </div>
          </div>

          {/* Table */}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>N. SAL</TableHead><TableHead>Descrizione</TableHead><TableHead>Cantiere</TableHead>
                  <TableHead>Data</TableHead><TableHead>Totale</TableHead><TableHead>Stato</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">Caricamento...</TableCell></TableRow>
                  ) : items.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">Nessun SAL</TableCell></TableRow>
                  ) : items.map(m => (
                    <TableRow key={m.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetail(m)} data-testid={`sal-row-${m.id}`}>
                      <TableCell className="text-sm font-medium">{m.sal_number || '-'}</TableCell>
                      <TableCell className="text-sm max-w-[260px] truncate">{m.description}{m.source_type === 'uploaded_excel' && <Badge variant="outline" className="ml-2 text-[10px]">XLS</Badge>}</TableCell>
                      <TableCell className="text-sm text-slate-600">{m.site_name || '-'}</TableCell>
                      <TableCell className="text-sm">{fmtDate(m.date)}</TableCell>
                      <TableCell className="text-sm font-semibold">{fmtEur(m.total_amount)}</TableCell>
                      <TableCell><Badge variant="outline" className={STATUS_META[m.status]?.cls}>{STATUS_META[m.status]?.label}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TOTALI MENSILI */}
        <TabsContent value="totali" className="space-y-4">
          <div className="flex items-center gap-2">
            <Label className="text-xs">Anno</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28" data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3].map(d => { const y = new Date().getFullYear() - d; return <SelectItem key={y} value={String(y)}>{y}</SelectItem>; })}
              </SelectContent>
            </Select>
          </div>
          {monthly && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Card className="border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Totale Globale {monthly.year}</p><p className="text-2xl font-bold text-green-700" data-testid="grand-total">{fmtEur(monthly.grand_total)}</p></CardContent></Card>
                <Card className="border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">SAL Fiumicino</p><p className="text-2xl font-bold text-slate-800">{fmtEur(monthly.grand_sal_fiumicino)}</p></CardContent></Card>
                <Card className="border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Matrici SES (Sarpom)</p><p className="text-2xl font-bold text-slate-800">{fmtEur(monthly.grand_ses_sarpom)}</p></CardContent></Card>
              </div>
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Fatturato per Mese (tutti i cantieri)</CardTitle></CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Mese</TableHead><TableHead>SAL Fiumicino</TableHead><TableHead>SES Sarpom</TableHead><TableHead>Totale</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {monthly.months.map(mo => (
                        <TableRow key={mo.month} className={mo.total === 0 ? 'text-slate-400' : ''}>
                          <TableCell className="text-sm font-medium">{MONTHS[mo.month - 1]}</TableCell>
                          <TableCell className="text-sm">{fmtEur(mo.sal_fiumicino)}</TableCell>
                          <TableCell className="text-sm">{fmtEur(mo.ses_sarpom)}</TableCell>
                          <TableCell className="text-sm font-semibold">{fmtEur(mo.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Fatturato per Cantiere</CardTitle></CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Cantiere</TableHead><TableHead>SAL Fiumicino</TableHead><TableHead>SES Sarpom</TableHead><TableHead>Totale</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {monthly.by_site.length === 0 ? (
                        <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-500">Nessun dato per {monthly.year}</TableCell></TableRow>
                      ) : monthly.by_site.map(s => (
                        <TableRow key={s.site_id}>
                          <TableCell className="text-sm font-medium">{s.site_name}</TableCell>
                          <TableCell className="text-sm">{fmtEur(s.sal_fiumicino)}</TableCell>
                          <TableCell className="text-sm">{fmtEur(s.ses_sarpom)}</TableCell>
                          <TableCell className="text-sm font-semibold">{fmtEur(s.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica SAL' : 'Nuovo SAL Fiumicino'}</DialogTitle><DialogDescription>Inserisci i dati del SAL e le righe di avanzamento lavori.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>N. SAL</Label><Input value={form.sal_number} onChange={(e) => setForm({ ...form, sal_number: e.target.value })} data-testid="form-sal-number" /></div>
            <div className="space-y-1"><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div className="space-y-1"><Label>Work Order</Label><Input value={form.work_order} onChange={(e) => setForm({ ...form, work_order: e.target.value })} /></div>
            <div className="space-y-1"><Label>Purchase Order</Label><Input value={form.purchase_order} onChange={(e) => setForm({ ...form, purchase_order: e.target.value })} /></div>
            <div className="col-span-2 space-y-1"><Label>Descrizione</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description" /></div>
            <div className="space-y-1"><Label>Cantiere</Label>
              <Select value={form.site_id || '__none__'} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                <SelectTrigger data-testid="form-site"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">Nessuno</SelectItem>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Progetto</Label>
              <Select value={form.project_id || '__none__'} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">Nessuno</SelectItem>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Righe</Label>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn"><Plus size={14} className="mr-1" /> Riga</Button>
            </div>
            <div className="space-y-2">
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end border-b border-slate-100 pb-2">
                  <div className="col-span-5 space-y-1"><Label className="text-[10px]">Descrizione</Label><Input value={l.description} onChange={(e) => setLine(i, 'description', e.target.value)} /></div>
                  <div className="col-span-1 space-y-1"><Label className="text-[10px]">U.M.</Label><Input value={l.unit} onChange={(e) => setLine(i, 'unit', e.target.value)} /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-[10px]">Qta</Label><Input type="number" value={l.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-[10px]">Prezzo</Label><Input type="number" value={l.unit_price} onChange={(e) => setLine(i, 'unit_price', e.target.value)} /></div>
                  <div className="col-span-1 space-y-1"><Label className="text-[10px]">K%</Label><Input type="number" value={l.k_line_pct} onChange={(e) => setLine(i, 'k_line_pct', e.target.value)} /></div>
                  <div className="col-span-1 flex justify-center"><Button size="icon" variant="ghost" className="text-red-500" onClick={() => removeLine(i)}><Trash2 size={14} /></Button></div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-2">
            <div className="space-y-1"><Label>K Totale %</Label><Input type="number" value={form.k_total_pct} onChange={(e) => setForm({ ...form, k_total_pct: e.target.value })} /></div>
            <div className="space-y-1"><Label>IVA %</Label><Input type="number" value={form.vat_pct} onChange={(e) => setForm({ ...form, vat_pct: e.target.value })} /></div>
            <div className="flex items-center gap-2 pt-6"><Checkbox checked={form.vat_included} onCheckedChange={(v) => setForm({ ...form, vat_included: !!v })} id="vatinc" /><Label htmlFor="vatinc">IVA inclusa</Label></div>
          </div>
          <div className="col-span-2 space-y-1 mt-2"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <p className="text-right text-sm font-semibold text-slate-700">Subtotale righe: {fmtEur(formTotal)}</p>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
            <Button onClick={save} disabled={!form.description} data-testid="save-sal-btn">{editingId ? 'Aggiorna' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2">SAL {detail.sal_number || ''} <Badge variant="outline" className={STATUS_META[detail.status]?.cls}>{STATUS_META[detail.status]?.label}</Badge></DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <p className="text-slate-700">{detail.description}</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Cantiere:</span> {detail.site_name || '-'}</div>
                  <div><span className="text-slate-500">Data:</span> {fmtDate(detail.date)}</div>
                  <div><span className="text-slate-500">Work Order:</span> {detail.work_order || '-'}</div>
                  <div><span className="text-slate-500">Imponibile:</span> {fmtEur(detail.net_amount)}</div>
                  <div><span className="text-slate-500">IVA:</span> {fmtEur(detail.vat_amount)}</div>
                  <div className="font-semibold"><span className="text-slate-500 font-normal">Totale:</span> {fmtEur(detail.total_amount)}</div>
                </div>
                {detail.lines?.length > 0 && (
                  <div className="border rounded overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow><TableHead>Descrizione</TableHead><TableHead>U.M.</TableHead><TableHead>Qta</TableHead><TableHead>Prezzo</TableHead><TableHead>Totale</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {detail.lines.map((l, i) => (
                          <TableRow key={i}><TableCell className="text-xs">{l.description}</TableCell><TableCell className="text-xs">{l.unit}</TableCell><TableCell className="text-xs">{l.quantity}</TableCell><TableCell className="text-xs">{fmtEur(l.unit_price)}</TableCell><TableCell className="text-xs">{fmtEur(l.line_total)}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {/* Attachments */}
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1">Allegati</p>
                  {(detail.attachments || []).map(a => (
                    <a key={a.id} href={`${process.env.REACT_APP_BACKEND_URL}${a.file_url}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 flex items-center gap-1"><Paperclip size={12} /> {a.original_name}</a>
                  ))}
                  {canManage && (
                    <>
                      <input ref={attachRef} type="file" className="hidden" onChange={(e) => handleAttach(e.target.files?.[0])} />
                      <Button size="sm" variant="ghost" className="mt-1 text-xs" onClick={() => attachRef.current?.click()}><Upload size={12} className="mr-1" /> Allega file</Button>
                    </>
                  )}
                </div>
                {detail.notes && <p className="text-xs text-slate-500 whitespace-pre-wrap">{detail.notes}</p>}
              </div>
              {canManage && (
                <DialogFooter className="flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => exportExcel(detail.id)}><Download size={14} className="mr-1" /> Excel</Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(detail)}><Edit size={14} className="mr-1" /> Modifica</Button>
                  {!detail.submitted_at && <Button size="sm" onClick={() => doSubmit(detail.id)} data-testid="submit-btn"><Send size={14} className="mr-1" /> Invia</Button>}
                  {detail.submitted_at && !detail.approved_at && isRole('admin', 'qhse') && <Button size="sm" onClick={() => doApprove(detail.id)} data-testid="approve-btn"><CheckCircle2 size={14} className="mr-1" /> Approva</Button>}
                  {detail.approved_at && !detail.invoice_number && isRole('admin', 'qhse') && <Button size="sm" onClick={() => doLinkInvoice(detail.id)}><Receipt size={14} className="mr-1" /> Collega Fattura</Button>}
                  {detail.approved_at && isRole('admin') && <Button size="sm" variant="ghost" className="text-amber-600" onClick={() => doRevoke(detail.id, 'approval')}>Revoca appr.</Button>}
                  {isRole('admin') && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(detail.id)}><Trash2 size={14} className="mr-1" /> Elimina</Button>}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload result */}
      <Dialog open={!!uploadResult} onOpenChange={(o) => !o && setUploadResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>SAL caricato</DialogTitle></DialogHeader>
          {uploadResult && (
            <div className="text-sm space-y-2">
              <p className="text-green-700">File elaborato: <strong>{uploadResult.original_filename}</strong></p>
              <p>Righe estratte: <strong>{uploadResult.lines?.length || 0}</strong></p>
              <p>Totale: <strong>{fmtEur(uploadResult.total_amount)}</strong></p>
              {uploadResult._parser_warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                  {uploadResult._parser_warnings.map((w, i) => <p key={i}>⚠ {w}</p>)}
                </div>
              )}
              <Button size="sm" onClick={() => { const m = items.find(x => x.id === uploadResult.id); setUploadResult(null); if (m) openEdit(m); }}>Apri e completa</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
